import type { SegmentDTO } from '@next-space/fe-api-idl';
import { TextType } from '@next-space/fe-api-idl';
import { last } from 'lodash-es';
import type { PhrasingContent } from 'mdast';
import { fromHtml2Mdast } from 'src/utils/ast-util';

import type { RecordCells } from '../../types';

export const parseHtmlToRecordCells = (html: string): RecordCells[] | undefined => {
  const mdast = fromHtml2Mdast(html);

  if (!mdast) return;

  if (mdast.type === 'root') {
    const { children } = mdast;
    const [first] = children;
    if (children.length === 1 && first && first.type === 'table') {
      const records = first.children.map((row) => ({
        cells: row.children.map((cell) => {
          const segments: SegmentDTO[] = [];

          const loop = (
            node: PhrasingContent,
            enhancer: SegmentDTO['enhancer'] = {},
            url?: string
          ) => {
            const setSegment = (segment: SegmentDTO) => {
              if (!segment.text) return;
              segments.push(segment);
            };

            if (node.type === 'text') {
              const segment: SegmentDTO = {
                type: TextType.TEXT,
                text: node.value,
                enhancer,
              };

              if (url) {
                segment.type = TextType.URL;
                segment.url = url;
              }

              setSegment(segment);
            }

            if (node.type === 'break') {
              const segment = last(segments);
              if (segment?.text) {
                segment.text += '\n';
              }
            }

            if (node.type === 'strong') {
              node.children.forEach((o) => loop(o, { ...enhancer, bold: true }, url));
            }

            if (node.type === 'emphasis') {
              node.children.forEach((o) => loop(o, { ...enhancer, italic: true }, url));
            }

            if (node.type === 'delete') {
              node.children.forEach((o) => loop(o, { ...enhancer, lineThrough: true }, url));
            }

            if (node.type === 'inlineCode') {
              const segment: SegmentDTO = {
                type: TextType.TEXT,
                text: node.value,
                enhancer: { ...enhancer, code: true },
              };
              if (url) {
                segment.type = TextType.URL;
                segment.url = url;
              }
              setSegment(segment);
            }

            if (node.type === 'inlineMath') {
              const segment: SegmentDTO = {
                type: TextType.EQUATION,
                text: node.value,
                enhancer,
              };
              setSegment(segment);
            }

            if (node.type === 'image') {
              // TODO
            }

            if (node.type === 'link') {
              if (node.children.length) {
                node.children.forEach((o) => loop(o, enhancer, node.url));
              } else {
                const segment: SegmentDTO = {
                  type: TextType.URL,
                  text: node.url,
                  url: node.url,
                  enhancer,
                };
                setSegment(segment);
              }
            }

            if (node.type === 'html') {
              if (node.value === '<br />') {
                const segment = last(segments);
                if (segment?.text) {
                  segment.text += '\n';
                }
              } else {
                const segment: SegmentDTO = {
                  type: TextType.TEXT,
                  text: node.value,
                  enhancer: {},
                };
                setSegment(segment);
              }
            }
          };

          cell.children.forEach((node) => {
            // @ts-ignore: html 转 markdown 会出现 type 为 paragraph 的情况，例如从 有道云笔记; 飞书会出现 type 为 heading
            if (node.type === 'paragraph' || node.type === 'heading') {
              // @ts-ignore: 同上
              node.children.forEach((_node) => loop(_node));
            } else {
              loop(node);
            }
          });

          return segments;
        }),
      }));

      if (records[0] && records[0].cells.every((s) => s.length === 0)) {
        records.shift();
      }

      return records;
    }
  }
};
